import NotificationsIcon from "@mui/icons-material/NotificationsOutlined";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { requestDetailPath } from "@vv/types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  connectNotificationsSocket,
  disconnectNotificationsSocket,
} from "../api/notifications.socket";
import { NOTIFICATION_TYPE_LABELS, type AppNotification } from "../domain/notification.types";
import { useNotificationsDispatch, useNotificationsSelector } from "../state/hooks";
import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from "../state/notifications.thunks";
import { notificationReceived } from "../state/notifications.slice";

export interface NotificationBellProps {
  apiBaseUrl: string;
}

function timeAgo(isoDate: string): string {
  const seconds = Math.floor((Date.now() - new Date(isoDate).getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function NotificationBell({ apiBaseUrl }: NotificationBellProps) {
  const dispatch = useNotificationsDispatch();
  const navigate = useNavigate();
  const notifications = useNotificationsSelector((state) => state.notifications.notifications);
  const unreadCount = useNotificationsSelector((state) => state.notifications.unreadCount);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    dispatch(fetchNotifications());
    dispatch(fetchUnreadCount());

    connectNotificationsSocket(apiBaseUrl, (notification) => {
      dispatch(notificationReceived(notification));
    });
    return () => disconnectNotificationsSocket();
  }, [dispatch, apiBaseUrl]);

  async function handleOpenNotification(notification: AppNotification) {
    setAnchorEl(null);
    navigate(requestDetailPath(notification.requestId));
    if (!notification.isRead) {
      await dispatch(markNotificationRead(notification.id));
    }
  }

  async function handleMarkAllRead() {
    await dispatch(markAllNotificationsRead());
  }

  return (
    <>
      <IconButton
        aria-label={`notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ width: 360, maxHeight: 480, overflowY: "auto" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Notifications
            </Typography>
            {unreadCount > 0 ? (
              <Button size="small" onClick={handleMarkAllRead}>
                Mark all read
              </Button>
            ) : null}
          </Stack>
          <Divider />
          {notifications.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No notifications yet.
            </Typography>
          ) : (
            <List disablePadding>
              {notifications.map((notification) => (
                <ListItemButton
                  key={notification.id}
                  onClick={() => handleOpenNotification(notification)}
                  sx={{
                    bgcolor: notification.isRead ? "transparent" : "action.hover",
                    alignItems: "flex-start",
                  }}
                >
                  <ListItemText
                    primary={NOTIFICATION_TYPE_LABELS[notification.type]}
                    secondary={timeAgo(notification.createdAt)}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
}
